import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LangProvider } from "@/contexts/LangContext";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import RegisterSubcontractorPage from "@/pages/RegisterSubcontractorPage";
import SubcontractorNotificationsPage from "@/pages/SubcontractorNotificationsPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import DashboardPage from "@/pages/DashboardPage";
import ProductionPage from "@/pages/ProductionPage";
import PaymentsPage from "@/pages/PaymentsPage";
import SubcontractorsPage from "@/pages/SubcontractorsPage";
import SettingsPage from "@/pages/SettingsPage";
import PriceRequestsPage from "@/pages/PriceRequestsPage";
import ShipmentsPage from "@/pages/ShipmentsPage";
import ClientsPage from "@/pages/ClientsPage";
import SubcontractorPortalPage from "@/pages/SubcontractorPortalPage";
import CommercialsPage from "@/pages/CommercialsPage";
import CommercialPortalPage from "@/pages/CommercialPortalPage";
import ClientPortalPage from "@/pages/ClientPortalPage";
import QualityPage from "@/pages/QualityPage";
import AiAgentPage from "@/pages/AiAgentSimplePage";
import AdminQuotesPage from "@/pages/AdminQuotesPage";
import MileagePage from "@/pages/MileagePage";
import MessagesPage from "@/pages/MessagesPage";
import MailPage from "@/pages/MailPage";
import TicketsAdminPage from "@/pages/TicketsAdminPage";
import TodoPage from "@/pages/TodoPage";
import Print3DOrdersPage from "@/pages/Print3DOrdersPage";
import SubcontractorTicketsPage from "@/pages/SubcontractorTicketsPage";
import Layout from "@/components/Layout";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-zinc-950">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-zinc-400 text-sm">Chargement...</span>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-zinc-950">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/register-subcontractor" element={user ? <Navigate to="/" replace /> : <RegisterSubcontractorPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/politique-de-confidentialite" element={<PrivacyPolicyPage />} />
      <Route path="/privacy" element={<Navigate to="/politique-de-confidentialite" replace />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                {user?.role === 'subcontractor' ? (
                  <>
                    <Route path="/" element={<SubcontractorPortalPage />} />
                    <Route path="/messages" element={<MessagesPage />} />
                    <Route path="/tickets" element={<SubcontractorTicketsPage />} />
                    <Route path="/sub/notifications" element={<SubcontractorNotificationsPage />} />
                  </>
                ) : user?.role === 'commercial' ? (
                  <>
                    <Route path="/" element={<CommercialPortalPage />} />
                    <Route path="/messages" element={<MessagesPage />} />
                  </>
                ) : user?.role === 'client' ? (
                  <>
                    <Route path="/" element={<ClientPortalPage />} />
                    <Route path="/messages" element={<MessagesPage />} />
                  </>
                ) : (
                  <>
                    <Route path="/" element={<DashboardPage />} />
                    <Route path="/production" element={<ProductionPage />} />
                    <Route path="/production/impression-3d" element={<Print3DOrdersPage />} />
                    <Route path="/payments" element={<PaymentsPage />} />
                    <Route path="/subcontractors" element={<SubcontractorsPage />} />
                    <Route path="/commercials" element={<CommercialsPage />} />
                    <Route path="/price-requests" element={<PriceRequestsPage />} />
                    <Route path="/shipments" element={<ShipmentsPage />} />
                    <Route path="/clients" element={<ClientsPage />} />
                    <Route path="/quality" element={<QualityPage />} />
                    <Route path="/ai-agent" element={<AiAgentPage />} />
                    <Route path="/admin-quotes" element={<AdminQuotesPage />} />
                    <Route path="/mileage" element={<MileagePage />} />
                    <Route path="/mail" element={<MailPage />} />
                    <Route path="/messages" element={<MessagesPage />} />
                    <Route path="/tickets" element={<TicketsAdminPage />} />
                    <Route path="/todo" element={<TodoPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                  </>
                )}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LangProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </LangProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
